import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getMarketHomeApi } from '@flipster/apis/v2/market';
import { MarketPlatformEnum } from '@flipster/apis/v2/market/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { hasKolInsightsLayoutNode } from './helpers';

export const kolInsightsBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  const marketHomeQueryAtom = atomWithQuery(() => ({
    queryKey: ['getMarketHomeApi', { platform: MarketPlatformEnum.Web }] as const,
    queryFn: () => getMarketHomeApi({ platform: MarketPlatformEnum.Web }),
    select: (res: Awaited<ReturnType<typeof getMarketHomeApi>>) => res.data,
    refetchOnMount: 'always' as const,
  }));

  const marketHomeAtom = atom((get) => get(marketHomeQueryAtom).data);
  const isLoadingAtom = atom((get) => get(marketHomeQueryAtom).isLoading);
  const isFetchingAtom = atom((get) => get(marketHomeQueryAtom).isFetching);
  const isErrorAtom = atom((get) => get(marketHomeQueryAtom).isError);

  const homeDataAtom = atom((get) => get(marketHomeAtom)?.data);

  const hasKolInsightsKeyAtom = atom((get) => {
    const data = get(homeDataAtom);
    if (!data) return false;
    return Object.prototype.hasOwnProperty.call(data, 'kolInsights');
  });

  /** Market section gate: home payload ready + layout node + kolInsights key (incl. empty). */
  const shouldShowMarketSectionAtom = atom((get) => {
    if (get(isErrorAtom)) return false;
    const home = get(marketHomeAtom);
    if (!home) return false;
    if (!hasKolInsightsLayoutNode(home.layout)) return false;
    return get(hasKolInsightsKeyAtom);
  });

  const kolInsightsAtom = atom((get) => get(homeDataAtom)?.kolInsights);
  const insightsAtom = atom((get) => get(kolInsightsAtom)?.insights ?? []);
  const isEmptyAtom = atom((get) => get(hasKolInsightsKeyAtom) && get(insightsAtom).length === 0);
  const refreshedAtAtom = atom((get) => get(kolInsightsAtom)?.refreshedAt);

  function refetch() {
    return store.get(marketHomeQueryAtom).refetch();
  }

  return {
    isLoadingAtom,
    isFetchingAtom,
    isErrorAtom,
    hasKolInsightsKeyAtom,
    shouldShowMarketSectionAtom,
    insightsAtom,
    isEmptyAtom,
    refreshedAtAtom,
    refetch,
  };
});

kolInsightsBunja.debugLabel = 'kolInsightsBunja';
